import { useEffect, useState } from "react";
import useFleetStore from "../stores/useFleetStore";
import { instance } from "../instance";
import { apiFetchAuthed } from "../apiHelpers";
import { routeAllowedOnInstance } from "../navigation/routeInstances";

// The easter-egg tie-in rides the route register, not an instance check: the
// game is Firefleet-only in routeInstances.ts, so the marquee line follows
// that single allocation. On SwarmSystems this is false and nothing fetches.
const GAME_ALLOWED = routeAllowedOnInstance('/pilot-simulator');

const Marquee = () => {
  const drones = useFleetStore((state) => state.drones);
  const flags = useFleetStore((state) => state.flags);
  const [highScore, setHighScore] = useState(null);
  const [spin, setSpin] = useState(0);

  // Today's top score, fetched once. Every failure means "no line", never an
  // error surface: the marquee must not hint that something is missing.
  useEffect(() => {
    if (!GAME_ALLOWED) return;
    apiFetchAuthed('/pilot-sim/scores', { silent: true })
      .then((b) => {
        const top = b && Array.isArray(b.daily) && b.daily[0];
        if (top && top.score > 0) setHighScore(top);
      })
      .catch(() => {});
  }, []);

  // Roughly every third rotation, and only for players who exist: subtle by
  // design, most people should not notice it right away. The swap happens on
  // animationiteration, the instant the loop seam sits at x=0, so the width
  // change never pops mid-scroll.
  const showHigh = highScore && spin % 3 === 2;
  const pilot = highScore && highScore.firstname
    ? `${highScore.firstname.toUpperCase()}${highScore.lastname ? ' ' + highScore.lastname[0].toUpperCase() + '.' : ''}`
    : 'RETIRED PILOT';

  return (
    <div
      className="relative overflow-hidden rounded-panel bg-surface-container-low shadow-recess"
    >
      {/* Gradient fade edges */}
      <div className="absolute left-0 top-0 bottom-0 w-16 bg-gradient-to-r from-surface-container-low to-transparent z-10 pointer-events-none" />
      <div className="absolute right-0 top-0 bottom-0 w-16 bg-gradient-to-l from-surface-container-low to-transparent z-10 pointer-events-none" />

      <div
        className="marquee-track flex py-2.5 items-center"
        onAnimationIteration={(e) => {
          // animationiteration BUBBLES: the flag dot's animate-pulse fires this
          // every 2s. Count only the track's own 35s marquee-scroll loop.
          if (e.animationName === 'marquee-scroll') setSpin((s) => s + 1);
        }}
      >
        {[1, 2].map((_, idx) => (
          <div key={idx} className="marquee-content flex items-center shrink-0">
            <span className="px-6 text-on-surface-variant whitespace-nowrap text-sm">
              Thanks for helping us stay organized with {instance.displayName}!
            </span>
            <span className="text-outline-variant">&middot;</span>
            <span className="px-6 text-on-surface-variant whitespace-nowrap text-sm">
              Total Drones: <span className="text-primary font-semibold">{drones.length}</span>
            </span>
            <span className="text-outline-variant">&middot;</span>
            <span className="px-6 whitespace-nowrap flex items-center gap-2 text-sm">
              {flags.length > 0 ? (
                <>
                  <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                  <span className="text-red-400">{flags.length} active {flags.length === 1 ? 'flag' : 'flags'}</span>
                </>
              ) : (
                <>
                  <span className="w-2 h-2 bg-green-500 rounded-full" />
                  <span className="text-green-400">Fleet healthy</span>
                </>
              )}
            </span>
            {showHigh && (
              <>
                <span className="text-outline-variant">&middot;</span>
                <span className="px-6 text-on-surface-variant whitespace-nowrap text-sm">
                  Today's high score: <span className="text-primary font-semibold">{highScore.score.toLocaleString()}</span> {pilot}
                </span>
              </>
            )}
            {instance.tagline && (
              <>
                <span className="text-outline-variant">&middot;</span>
                <span className="px-6 text-primary font-bold whitespace-nowrap tracking-wide text-sm">
                  {instance.tagline}
                </span>
              </>
            )}
            <span className="text-outline-variant">&middot;</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Marquee;
